import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { useAuth } from "@/context/AuthContext";
import { capacidades, accionesExpediente } from "@/lib/capacidades";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OnboardingBadge } from "@/pages/Organizaciones";
import ExpedienteTab from "@/components/organizaciones/ExpedienteTab";
import RepresentacionTab from "@/components/organizaciones/RepresentacionTab";
import DocumentosTab from "@/components/organizaciones/DocumentosTab";
import SitiosTab from "@/components/organizaciones/SitiosTab";
import UsuariosExternosTab from "@/components/organizaciones/UsuariosExternosTab";
import ZonaPeligro from "@/components/lifecycle/ZonaPeligro";
import ConfiguracionLifecycle from "@/components/lifecycle/ConfiguracionLifecycle";
import { ArrowLeft, Send, CheckCircle2, Settings, Info } from "lucide-react";

export default function OrganizacionDetalle() {
  const { oid } = useParams();
  const navigate = useNavigate();
  const { user, isSupervisor } = useAuth();
  const [d, setD] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [tab, setTab] = useState("expediente");
  const [configAbierta, setConfigAbierta] = useState(false);

  useEffect(() => {
    if (!isSupervisor) {
      notifyError("Sólo un supervisor de Galarya administra organizaciones.");
      navigate("/consola-v3", { replace: true });
    }
  }, [isSupervisor, navigate]);

  const load = useCallback(() => {
    if (!isSupervisor) return;
    api.get(`/organizations/${oid}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [oid, isSupervisor]);
  useEffect(() => { load(); api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {}); }, [load]);

  const accion = async (ruta, mensaje) => {
    try {
      await api.post(`/organizations/${oid}/perfil/${ruta}`);
      notifyOk(mensaje);
      load();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleEstadoOrg = async () => {
    const act = d.organization.estado === "Inactivo" ? "activar" : "desactivar";
    try {
      await api.post(`/organizations/${oid}/${act}`);
      notifyOk(act === "activar" ? "Organización activada." : "Organización desactivada.");
      load();
      return true;
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  if (!d) return <p className="text-slate-400">Cargando organización…</p>;
  const onboarding = d.perfil?.onboarding_status || "borrador";
  // UI-015: el vocabulario depende del tipo REAL de la organización (no se llama "institucional"
  // a lo que es residencial). No cambia roles ni permisos: sólo el texto y la visibilidad.
  const esInstitucional = d.organization.tipo === "institucional";
  const accionesExp = accionesExpediente(onboarding, capacidades(user));
  const conAcceso = (d.usuarios_externos || []).length > 0;
  const inactiva = d.organization.estado === "Inactivo";
  const numUsuarios = (d.usuarios_externos || []).length;

  return (
    <div className="space-y-6" data-testid="organizacion-detalle">
      <button onClick={() => navigate("/organizaciones")} data-testid="volver-organizaciones"
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#012472]">
        <ArrowLeft className="h-4 w-4" /> Organizaciones
      </button>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#012472] break-words">{d.organization.nombre}</h1>
            <OnboardingBadge estado={onboarding} testId="detalle-onboarding-badge" />
            {conAcceso && onboarding !== "validado" && (
              <span data-testid="detalle-badge-expediente-pendiente"
                className="text-[10px] font-semibold uppercase tracking-wide leading-tight rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 max-w-full">
                Acceso activo · Expediente pendiente
              </span>
            )}
            {/* UI-008: sólo es REVALIDACIÓN si ya existió una validación previa acreditada
                (`expediente_validado_en`). En el primer ciclo es una validación, no una re-. */}
            {onboarding === "en_revision" && (
              <span data-testid="detalle-revalidacion" className="text-[10px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
                {d.perfil?.expediente_validado_en ? "Revalidación administrativa pendiente" : "Validación administrativa pendiente"}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {esInstitucional ? "Institucional" : "Residencial"} ·
            {" "}{d.sitios.length} sitio(s) · {d.usuarios_externos.length} {esInstitucional ? "usuario(s) institucional(es)" : "usuario(s) con acceso"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {accionesExp.puedeEnviarARevision && (
            <Button variant="outline" className="rounded-xl flex-1 sm:flex-none" data-testid="enviar-revision-button"
              onClick={() => accion("enviar-revision", "Expediente enviado a revisión.")}>
              <Send className="h-4 w-4 mr-1.5" /> Enviar a revisión
            </Button>
          )}
          {accionesExp.puedeValidar && (
            <Button className="rounded-xl flex-1 sm:flex-none bg-[#012472] hover:bg-[#011c5a] text-white" data-testid="validar-expediente-button"
              onClick={() => accion("validar", "Expediente validado.")}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Validar expediente
            </Button>
          )}
          {isSupervisor && (
            <Button variant="ghost" onClick={() => setConfigAbierta(true)}
              data-testid="org-configuracion-button" aria-label="Configuración de la organización"
              title="Configuración"
              className="rounded-xl h-11 w-11 p-0 shrink-0 text-slate-500 hover:text-[#012472] hover:bg-slate-100">
              <Settings className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {inactiva && (
        <div data-testid="org-banner-inactiva"
          className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-2.5">
          <Info className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600">
            Organización inactiva. Sus {esInstitucional ? "usuarios institucionales" : "usuarios con acceso"} no tienen acceso.
            <span className="block text-xs text-slate-400 mt-0.5">Gestionar en Configuración.</span>
          </p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-xl flex-wrap h-auto gap-1 p-1">
          {[["expediente", "Expediente"], ["representacion", "Representación"], ["documentos", "Documentos"],
            ["sitios", "Sitios"], ["usuarios", esInstitucional ? "Usuarios institucionales" : "Usuarios con acceso"]].map(([v, label]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-btn-${v}`}
              className="text-xs sm:text-sm data-[state=active]:bg-[#012472] data-[state=active]:text-white data-[state=active]:shadow-sm">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-6">
          <TabsContent value="expediente">
            <ExpedienteTab key={d.perfil?.actualizado_en || "p"} oid={oid} perfil={d.perfil}
              faltantes={d.faltantes} clientes={clientes} onReload={load} onIrATab={setTab} />
          </TabsContent>
          <TabsContent value="representacion">
            <RepresentacionTab oid={oid} onReload={load} />
          </TabsContent>
          <TabsContent value="documentos">
            <DocumentosTab oid={oid} />
          </TabsContent>
          <TabsContent value="sitios">
            <SitiosTab oid={oid} orgNombre={d.organization.nombre} orgTipo={d.organization.tipo} sitios={d.sitios}
              vinculables={d.sitios_vinculables} onReload={load} />
          </TabsContent>
          <TabsContent value="usuarios">
            <UsuariosExternosTab oid={oid} sitios={d.sitios} usuarios={d.usuarios_externos}
              esInstitucional={esInstitucional}
              onboardingStatus={onboarding} faltantes={d.faltantes} onReload={load} />
          </TabsContent>
        </div>
      </Tabs>

      {isSupervisor && (
        <ConfiguracionLifecycle open={configAbierta} onOpenChange={setConfigAbierta}
          titulo={`Configuración de ${d.organization.nombre}`} inactivo={inactiva}
          testPrefix="org" labelActivar="Activar organización" labelDesactivar="Desactivar organización"
          explicacion="Desactivar retira el acceso de sus usuarios institucionales y conserva todo el historial, el expediente y la auditoría."
          onToggle={toggleEstadoOrg}
          confirmar={{
            desactivar: {
              titulo: `Desactivar ${d.organization.nombre}`,
              texto: "Los usuarios institucionales de esta organización perderán acceso inmediatamente. El historial, expediente, sitios y casos se conservarán.",
              nota: numUsuarios > 0
                ? `Afecta a ${numUsuarios} usuario(s) institucional(es) de esta organización.` : null,
              boton: "Desactivar organización",
            },
            activar: {
              titulo: `Activar ${d.organization.nombre}`,
              texto: "Los usuarios institucionales activos podrán volver a iniciar sesión según sus permisos y sitios autorizados.",
              boton: "Activar organización",
            },
          }}>
          <ZonaPeligro basePath={`/organizations/${oid}`} tipoLabel="organización"
            labelToggle="Eliminar organización permanentemente"
            confirmarCon="el nombre exacto de la organización" confirmarValor={d.organization.nombre}
            testPrefix="org" onDeleted={() => navigate("/organizaciones")} />
        </ConfiguracionLifecycle>
      )}
    </div>
  );
}
